// src/httpConfig/config/axios.js
import axios from 'axios';
import { jsonApiTransformer } from '../transformers/jsonApiTransformer.js';
import { setupRequestInterceptors, setupResponseInterceptors } from './api-interceptors.js';
import { installPreviewTransport } from '@/collector/previewReplay.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * SINGLE AXIOS INSTANCE ARCHITECTURE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * CRITICAL: We use ONE base axios instance to prevent multiple Laravel session
 * cookies which cause "400 Request Header Too Large" errors.
 *
 * Different "clients" are created using Object.create() which:
 * ✅ Maintains full axios instance compatibility (works with Pinia-ORM)
 * ✅ Shares the same underlying instance (single session cookie)
 * ✅ Allows method overrides for automatic header injection
 * ✅ Preserves interceptors, defaults, and all axios properties
 *
 * Third-party library compatibility:
 * ✅ Pinia-ORM Axios - calls axios.request() directly
 * ✅ axios-retry - modifies interceptors
 * ✅ Any library expecting real axios instance
 */

// ═══════════════════════════════════════════════════════════════════════════
// BASE AXIOS INSTANCE (The One True Instance™)
// ═══════════════════════════════════════════════════════════════════════════

const baseAxiosInstance = axios.create({
  baseURL: `${import.meta.env.VITE_API_BASE_URL}/api/v1`,//import.meta.env.MODE === 'development' ? '/api/v1' : `${import.meta.env.VITE_API_BASE_URL}/api/v1`,//'/api/v1',//`${import.meta.env.VITE_API_BASE_URL}/api/v1`,//'/api/v1',//`${import.meta.env.VITE_API_BASE_URL}/api/v1`,//'/api/v1',// `${import.meta.env.VITE_API_BASE_URL}/api/v1`,
  withCredentials: true,
  withXSRFToken: true,
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
  },
});

// Setup interceptors ONCE on the base instance
setupResponseInterceptors(baseAxiosInstance);
setupRequestInterceptors(baseAxiosInstance);

// The transport under every request: the network, except while a collector's
// offline supplier preview is on screen, when reads are answered from the copy
// saved on the phone (collector/previewReplay.js, supplier-preview-offline L-E).
installPreviewTransport(baseAxiosInstance);

// ═══════════════════════════════════════════════════════════════════════════
// CLIENT TYPE MARKER SYSTEM
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Add a request interceptor that injects headers based on _clientType marker
 * This runs AFTER the interceptors in api-interceptors.js
 */
baseAxiosInstance.interceptors.request.use((config) => {
  const clientType = config._clientType;
  const method = (config.method || 'get').toLowerCase();
  // Per JSON:API Section 1 + the wallet API handoff, Content-Type is sent ONLY on requests
  // that actually carry a body. Adding it to GET/DELETE is harmless for most servers
  // but technically wrong and trips strict JSON:API media-type validators.
  const hasBody = method === 'post' || method === 'put' || method === 'patch';

  if (clientType === 'jsonAPI') {
    if (hasBody) {
      config.headers['Content-Type'] = config.headers['Content-Type'] || 'application/vnd.api+json';
    }
    config.headers['Accept'] = config.headers['Accept'] || 'application/vnd.api+json';
  } else if (clientType === 'api' || clientType === 'auth') {
    if (hasBody) {
      config.headers['Content-Type'] = config.headers['Content-Type'] || 'application/json';
    }
    config.headers['Accept'] = config.headers['Accept'] || 'application/json';
  }

  // Remove the marker so it doesn't get sent to server
  delete config._clientType;

  return config;
}, (error) => {
  return Promise.reject(error);
});

// ═══════════════════════════════════════════════════════════════════════════
// FLAVORED CLIENT CREATION
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Creates a "flavored" axios instance that:
 * 1. Inherits from base instance (shares session, interceptors, defaults)
 * 2. Overrides HTTP methods to inject _clientType marker
 * 3. Maintains full axios API compatibility
 *
 * @param {Object} baseInstance - The base axios instance
 * @param {string} clientType - Client type marker ('jsonAPI', 'api', 'auth')
 * @param {Function} responseTransform - Optional response transformer
 * @returns {Object} Flavored axios instance
 */
function createFlavoredInstance(baseInstance, clientType, responseTransform = null) {
  // Use Object.create to inherit from base instance
  // This gives us a new object with baseInstance as its prototype
  const flavoredInstance = Object.create(baseInstance);

  // Store client type and transformer as non-enumerable properties
  Object.defineProperty(flavoredInstance, '_clientType', {
    value: clientType,
    writable: false,
    enumerable: false,
    configurable: false,
  });

  Object.defineProperty(flavoredInstance, '_responseTransform', {
    value: responseTransform,
    writable: false,
    enumerable: false,
    configurable: false,
  });

  // Override HTTP methods to inject client type marker.
  //
  // axios signatures differ by method, so we can't just "find the last object":
  //   request(config)                       → config at arg 0
  //   get/delete/head/options(url, config)  → config at arg 1
  //   post/put/patch(url, data, config)     → config at arg 2  ← body is arg 1
  //
  // The previous "last object" heuristic mis-targeted patch/post/put, merging
  // _clientType into the request body and starving the interceptor of the
  // marker — which broke JSON:API Accept/Content-Type headers and leaked
  // _clientType into the payload sent to the server.
  const CONFIG_SLOT = {
    request: 0,
    get: 1, delete: 1, head: 1, options: 1,
    post: 2, put: 2, patch: 2,
  };
  const BODY_METHODS = new Set(['post', 'put', 'patch']);
  const httpMethods = Object.keys(CONFIG_SLOT);

  // Stamp the JSON:API Accept/Content-Type headers directly on the config we hand
  // to axios. We used to do this from a request interceptor, but interceptor order
  // and per-instance defaults made it possible for the headers to be missing in
  // DevTools (DELETE in particular surfaced the axios default Accept). Setting
  // them on config.headers up-front is deterministic and survives axios's
  // mergeConfig because per-request headers always win over defaults.
  function buildHeaders(existingHeaders, type, method) {
    const headers = { ...(existingHeaders || {}) };
    const hasBody = BODY_METHODS.has(method);
    if (type === 'jsonAPI') {
      if (!headers.Accept) headers.Accept = 'application/vnd.api+json';
      if (hasBody && !headers['Content-Type']) headers['Content-Type'] = 'application/vnd.api+json';
    } else if (type === 'api' || type === 'auth') {
      if (!headers.Accept) headers.Accept = 'application/json';
      if (hasBody && !headers['Content-Type']) headers['Content-Type'] = 'application/json';
    }
    return headers;
  }

  httpMethods.forEach((method) => {
    flavoredInstance[method] = function (...args) {
      const modifiedArgs = [...args];
      const slot = CONFIG_SLOT[method];
      const existing = modifiedArgs[slot];
      const baseConfig = existing && typeof existing === 'object' ? existing : {};
      modifiedArgs[slot] = {
        ...baseConfig,
        // Header is the canonical signal now — we keep _clientType so the
        // interceptor at baseAxiosInstance can still consume it as a backstop.
        headers: buildHeaders(baseConfig.headers, clientType, method),
        _clientType: clientType,
      };

      const promise = baseInstance[method].apply(baseInstance, modifiedArgs);
      return responseTransform ? promise.then(responseTransform) : promise;
    };
  });

  // Ensure interceptors and defaults point to base instance
  // (Object.create already handles this via prototype chain, but be explicit)
  Object.defineProperty(flavoredInstance, 'interceptors', {
    get() {
      return baseInstance.interceptors;
    },
    enumerable: true,
  });

  Object.defineProperty(flavoredInstance, 'defaults', {
    get() {
      return baseInstance.defaults;
    },
    enumerable: true,
  });

  return flavoredInstance;
}

// ═══════════════════════════════════════════════════════════════════════════
// JSON:API RESPONSE TRANSFORMER
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Transform JSON:API responses to include deserialized data
 * This runs after all interceptors
 */
function transformJsonApiResponse(response) {
  if (!response.data) {
    return response;
  }

  try {
    const deserialized = jsonApiTransformer.deserialize(response.data);

    // Create new response object with transformed data structure
    return {
      ...response,
      data: {
        data: deserialized,
        meta: response.data.meta || {},
        links: response.data.links || {},
      },
    };
  } catch {
    // Return original response if transformation fails
    return response;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// EXPORTED CLIENTS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * JSON:API Client
 * - Automatically sets JSON:API headers
 * - Transforms responses using JSON:API deserializer
 * - Full axios instance (works with Pinia-ORM)
 */
export const jsonApiClient = createFlavoredInstance(
  baseAxiosInstance,
  'jsonAPI',
  transformJsonApiResponse,
);

/**
 * Standard API Client
 * - Sets standard JSON headers
 * - No response transformation
 * - Full axios instance (works with Pinia-ORM)
 */
export const apiClient = createFlavoredInstance(
  baseAxiosInstance,
  'api',
);

/**
 * Auth Client
 * - Same as apiClient (standard JSON headers)
 * - Semantic distinction for authentication endpoints
 * - Full axios instance (works with Pinia-ORM)
 */
export const authClient = createFlavoredInstance(
  baseAxiosInstance,
  'auth',
);

/**
 * Base Axios Instance
 * - Direct access to the underlying instance
 * - Use for advanced scenarios or when you need unmodified behavior
 * - This is what all clients ultimately use
 */
export const axiosInstance = baseAxiosInstance;

/**
 * Default export (base instance)
 */
export default baseAxiosInstance;

// ═══════════════════════════════════════════════════════════════════════════
// USAGE EXAMPLES & DOCUMENTATION
// ═══════════════════════════════════════════════════════════════════════════

/*

// ─────────────────────────────────────────────────────────────────────────
// STANDARD USAGE
// ─────────────────────────────────────────────────────────────────────────

// JSON:API requests (automatic headers + transformation)
const response = await jsonApiClient.get('/users', {
  params: { include: 'posts' }
});
console.log(response.data.data); // Deserialized data
console.log(response.data.meta); // Pagination, etc.

// Standard API requests
const result = await apiClient.post('/endpoint', { key: 'value' });

// Authentication requests
await authClient.post('/login', credentials);

// ─────────────────────────────────────────────────────────────────────────
// PINIA-ORM AXIOS USAGE
// ─────────────────────────────────────────────────────────────────────────

import { createPiniaOrmAxios } from 'pinia-orm-axios';
import { jsonApiClient } from '@/httpConfig/config/axios';

// Configure Pinia-ORM with jsonApiClient
const piniaOrmAxios = createPiniaOrmAxios({
  axios: jsonApiClient, // ✅ Works because it's a real axios instance
  dataTransformer: (response) => {
    // Response is already transformed by jsonApiClient
    return response.data.data;
  }
});

// Use in Pinia store
pinia.use(piniaOrmAxios);

// ─────────────────────────────────────────────────────────────────────────
// REPOSITORY USAGE
// ─────────────────────────────────────────────────────────────────────────

import { AxiosRepository } from 'pinia-orm-axios';

class UserRepository extends AxiosRepository {
  use = User;

  apiConfig = {
    save: true,
    actions: {
      fetchProfile() {
        return this.api().get('/users/-actions/profile', {
          params: { include: 'telegramUser,memberships' }
        });
      }
    }
  };
}

// Usage
const userRepo = useAxiosRepo(User);
userRepo.setAxios(jsonApiClient); // ✅ Works perfectly
const response = await userRepo.api().fetchProfile();

// ─────────────────────────────────────────────────────────────────────────
// ADVANCED: Direct instance access
// ─────────────────────────────────────────────────────────────────────────

// Access shared interceptors
jsonApiClient.interceptors.request.use(...);
apiClient.interceptors.response.use(...);

// Modify shared defaults
axiosInstance.defaults.timeout = 30000;

// All clients share the modification because they share the same base instance

// ─────────────────────────────────────────────────────────────────────────
// VERIFICATION
// ─────────────────────────────────────────────────────────────────────────

// Verify all clients share same base
console.log(jsonApiClient.defaults === apiClient.defaults); // true
console.log(jsonApiClient.interceptors === authClient.interceptors); // true

// Check cookie count (should be 2 max: session + XSRF)
console.log(document.cookie.split(';').length);

*/
